import { useDispatch, useSelector } from 'react-redux'
import useSpinner from '../../context/hooks/useSpinner'
import { getCurrentMenuId } from '../../selectors/appState'
import { getUserUuidFromOrderInProgress } from '../../selectors/order'
import { getTaxExemptData } from '../../selectors/cart'
import { getCurrentMenu, getKitchenPrintingEnabled, getUseTaxClasses } from '../../selectors/menus'
import { calculateSplitOrderTotal, createSplitOrder } from '../Apis'
import { formatSplitItems } from '../../hooks/useRemoteOrderTotal'
import { PAYMENT_TYPES, PAYMENT_TYPES_INTERNAL } from '../../utils/paymentTypes'
import {
  createOrderParams,
  getUserNameFromCard,
  makeCreateOrderLineItems,
  makeLineItemData,
  makeUserAttributes,
} from '../../utils/orderUtils'
import { orderDetailsRequested, saveOrder, selectOrder, setOrderInProgressWithId, tabOrdersRequested } from '../../actions/order'
import { ToastManager } from '../../utils/toastManager'
import { setKitchenPrintErrorDialogOpen } from '../../VNDialogs/ActionCreators'
import { useVerifyCurrentAttendant } from './useVerifyCurrentAttendant'
import { getActiveRevenueCenterId } from '../../selectors/revenueCenter'
import usePriceAdjustment from '../../VNItems/hooks/usePriceAdjustment'
import { isCreditType, STADIUM_ORDER_STATES } from '../../utils/orderStates'
import moment from 'moment'
import { getBackbarMode, getNetworkAvailable } from '../../selectors/config'
import { VNCFD_TRANSACTION_RESULT } from '../../VNCustomerFacingDisplay/ActionTypes'
import createDataDogLog from '../../utils/datadog'
import { getDeviceMode } from '../../VNMode/Selectors'
import { MODES } from '../../VNMode/Reducer'
import { setCurrentPaymentFlow } from '../../VNCustomerFacingDisplay/ActionCreators'
import { CFD_POS_PAYMENT_FLOWS } from '../../VNCustomerFacingDisplay/Enums'
import StadiumRemote from '../../remote/stadiumRemote'
import { sendCFDScreenNav, sendUpdateCart } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { CFD_SCREEN } from '../../VNCustomerFacingDisplay/Reducer'
import { invoiceNumber } from '../../utils/orderNumbers'

export const useSplitTab = () => {
  const { startSpinner, stopSpinner } = useSpinner()
  const dispatch = useDispatch()

  const menuUuid = useSelector(getCurrentMenuId)
  const revenueCenterUuid = useSelector(getActiveRevenueCenterId)
  const currentMenu = useSelector(getCurrentMenu)
  const userUuid = useSelector(getUserUuidFromOrderInProgress)
  const cloudPrintingEnabled = useSelector(getKitchenPrintingEnabled)

  const useTaxClasses = useSelector(getUseTaxClasses)
  const taxExempt = useSelector(getTaxExemptData)?.taxExempt || false
  const isBackBar = useSelector(getBackbarMode)
  const cfdMode = useSelector(getDeviceMode) === MODES.POS
  const isOnline = useSelector(getNetworkAvailable)

  const { retotalOfflineOrder } = usePriceAdjustment()

  const { verifyCurrentAttendant } = useVerifyCurrentAttendant()

  const getSplitOrderTotal = async (cartItems) => {
    startSpinner()
    try {
      const result = await calculateSplitOrderTotal(cartItems, menuUuid, userUuid, taxExempt, useTaxClasses)
      stopSpinner()
      return result
    } catch (error) {
      stopSpinner()
      createDataDogLog('error', 'Error calculating split order total', error)
      return null
    }
  }

  const splitOrder = async (
    lineItems,
    newOrderNumber,
    newOrderUuid,
    originalOrderId,
    payments,
    amount,
    userAttributes,
    userName,
    originalOrder,
  ) => {
    startSpinner()
    try {
      if (!originalOrder.wasCreatedInStadium && isOnline) {
        await StadiumRemote.createPosOrder(createOrderParams(originalOrder))
        dispatch(saveOrder({ ...originalOrder, wasCreatedInStadium: true }))
      }

      const orderMenuItems = formatSplitItems(lineItems, false)
      const result = await createSplitOrder(
        orderMenuItems,
        newOrderNumber,
        newOrderUuid,
        originalOrderId,
        payments,
        amount,
        userAttributes,
        userName,
      )
      stopSpinner()
      return result
    } catch (error) {
      createDataDogLog('error', 'Error creating split order', error)
      stopSpinner()
      ToastManager.error(error.message || 'Failed to create split order', { autoClose: false })
    }
  }

  const initiateTab = async (
    orderDetails,
    tabName,
    userAttributes,
    payments,
    paymentId,
    onClose,
    isNewAuth = false,
    isAddPaymentLater = false,
    isSavedAuth = false,
  ) => {
    if (!tabName) return

    if (orderDetails?.offline) {
      startOfflineTabOrder({
        tabName,
        paymentId,
        cardReaderData: isNewAuth ? payments : orderDetails?.cardReaderData,
        paymentType: !isAddPaymentLater && PAYMENT_TYPES.CREDIT_CARD,
        orderNumber: orderDetails?.orderNumber,
        orderUuid: orderDetails?.uuid,
        lineItems: orderDetails?.itemIds,
        isSavedAuth,
        isAddPaymentLater,
        isNewAuth,
        originalOrder: orderDetails.oldOrder,
      })
      changeOriginalOfflineTabOrder(orderDetails?.unselectedItems, orderDetails?.oldOrder)

      dispatch(selectOrder(orderDetails?.oldOrder?.uuid))

      if (onClose) {
        onClose()
      }

      ToastManager.successToastWithButton(`Split Tab ${orderDetails.orderNumber} created`, 'View', () => {
        dispatch(selectOrder(orderDetails?.uuid))
      })

      if (!isOnline && cloudPrintingEnabled) {
        dispatch(setKitchenPrintErrorDialogOpen(true))
      }

      if (cfdMode) {
        dispatch({
          type: VNCFD_TRANSACTION_RESULT,
          data: null,
        })
        dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
        if (orderDetails?.unselectedItems && orderDetails?.unselectedItems.length > 0) {
          sendCFDScreenNav(CFD_SCREEN.CART)
        } else {
          sendCFDScreenNav(CFD_SCREEN.IDLE)
        }
        sendUpdateCart(orderDetails?.unselectedItems)
      }

      return
    }

    // !Online flow here

    // this means that the user is coming from the auth screen
    let authPayments = []
    let newUserAttributes = null
    if (isNewAuth) {
      newUserAttributes = makeUserAttributes(payments, PAYMENT_TYPES_INTERNAL.SAVED_CARD)
      // makeUserAttributes doesn't return all the fields we need. Todo: make order utils and order sagas makeUserAttributes functions match.
      newUserAttributes.transType = payments?.transType || 'auth'
      newUserAttributes.cardHolderName = payments?.cardHolderName
      newUserAttributes.freedompayInvoiceNumber = payments?.invoiceNumber
      newUserAttributes.preservePaymentAfterReopen = isBackBar

      authPayments.push({
        paymentId,
        paymentType: PAYMENT_TYPES.CREDIT_CARD,
        amountInCents: orderDetails?.remoteOrderTotal?.totalAmountInCents,
        orderNumber: orderDetails?.orderNumber,
        userAttributes: newUserAttributes,
        shortDescription: `${newUserAttributes.freedompayIssuerName} ${newUserAttributes.last_4}`,
      })
    }

    if (isSavedAuth) {
      newUserAttributes = {
        transType: 'auth',
      }
      authPayments = [
        {
          amountInCents: orderDetails?.remoteOrderTotal?.totalAmountInCents,
        },
      ]

      if (!isCreditType(payments[0].paymentType)) {
        newUserAttributes = null
        authPayments[0].paymentType = payments[0].paymentType
        authPayments[0].shortDescription = payments[0].shortDescription
      }

      // new order needs new invoice number when it's online approval
      if (!payments[0].userAttributes?.isOfflineApproval && isCreditType(payments[0].paymentType)) {
        // when creating a new auth not in LTM, switch payment to token type`
        authPayments[0].paymentType = 'shift4_token'
        authPayments[0].token = payments[0].userAttributes?.token
        authPayments[0].userAttributes = {
          freedompayIssuerName: payments[0].userAttributes?.freedompayIssuerName,
          last4: payments[0].userAttributes?.last4,
          freedompayInvoiceNumber: invoiceNumber(orderDetails?.orderNumber),
          transType: 'auth',
          preservePaymentAfterReopen: isBackBar,
        }
      }
    }

    if (isAddPaymentLater) {
      authPayments = []
      newUserAttributes = null
    }

    const result = await splitOrder(
      orderDetails.itemIds,
      orderDetails.orderNumber,
      orderDetails?.uuid,
      orderDetails?.oldOrder?.uuid,
      authPayments,
      orderDetails?.amount,
      newUserAttributes,
      tabName,
      orderDetails?.oldOrder,
    )

    if (!result) {
      onClose()
      return
    }

    // request new order with latest details
    dispatch(orderDetailsRequested(orderDetails?.uuid))
    // request original order with latest details
    dispatch(orderDetailsRequested(orderDetails?.oldOrder?.uuid))
    dispatch(selectOrder(orderDetails?.oldOrder?.uuid))
    if (onClose) {
      onClose()
    }

    dispatch(tabOrdersRequested())
    ToastManager.successToastWithButton(`Split Tab ${orderDetails.orderNumber} created`, 'View', () => {
      dispatch(orderDetailsRequested(orderDetails?.uuid))
      dispatch(selectOrder(orderDetails?.uuid))
    })

    if (cfdMode) {
      dispatch({
        type: VNCFD_TRANSACTION_RESULT,
        data: null,
      })
      dispatch(setCurrentPaymentFlow(CFD_POS_PAYMENT_FLOWS.UNSET, true))
      if (orderDetails?.unselectedItems && orderDetails?.unselectedItems.length > 0) {
        sendCFDScreenNav(CFD_SCREEN.CART)
      } else {
        sendCFDScreenNav(CFD_SCREEN.IDLE)
      }
      sendUpdateCart(orderDetails?.unselectedItems)
    }
  }

  const startOfflineTabOrder = ({
    tabName,
    paymentId,
    cardReaderData,
    paymentType = null,
    orderNumber,
    orderUuid,
    lineItems,
    originalOrder,
    isSavedAuth = false,
    isNewAuth = false,
    isAddPaymentLater = false,
  }) => {
    const { subtotalAmountInCents, taxAmountInCents, amountInCents, balanceDueInCents, serviceFee, taxRulesBreakdown } =
      retotalOfflineOrder(lineItems)
    const itemModels = makeLineItemData(lineItems)
    const employeeId = verifyCurrentAttendant(false)
    const submittedAt = moment(Date.now()).toISOString()

    let userAttributes
    let userName

    let payment = {
      paymentId,
      paymentType,
      amountInCents,
      orderNumber,
      tipAmountInCents: 0,
    }

    if (isSavedAuth) {
      payment = originalOrder?.payments[0]
      payment.amountInCents = amountInCents
    }

    if (isNewAuth && cardReaderData) {
      userAttributes = makeUserAttributes(cardReaderData, PAYMENT_TYPES_INTERNAL.SAVED_CARD)
      userAttributes.transType = cardReaderData?.transType || 'auth'
      userName = getUserNameFromCard(cardReaderData)
    }

    if (isCreditType(paymentType) && !isSavedAuth) {
      payment.userAttributes = userAttributes
      payment.userName = userName
      payment.cardType = userAttributes?.freedompayIssuerName
      payment.label = cardReaderData?.cardType
      payment.shortDescription = `${userAttributes?.freedompayIssuerName} ${userAttributes?.last_4}`
    }

    const createOrderParams = {
      paymentId,
      orderMenuItems: makeCreateOrderLineItems(itemModels),
      standMenuUuid: menuUuid,
      clientCreatedAt: submittedAt,
      orderNumber,
      webOrder: true,
      uuid: orderUuid,
      employeeId,
      state: STADIUM_ORDER_STATES.TABBED_OFFLINE,
      userName: tabName,
      discountAmountInCents: 0,
    }

    if (isBackBar && payment?.userAttributes) {
      payment.userAttributes.preservePaymentAfterReopen = true
    }

    const orderData = {
      ...createOrderParams,
      itemModels,
      menuSubtype: currentMenu?.subtype,
      revenueCenterUuid,
      revenueName: currentMenu?.standName,
      submittedAt: submittedAt,
      tabName,
      payments: [payment],
      subtotalAmountInCents,
      cardReaderData: isSavedAuth ? originalOrder?.cardReaderData : cardReaderData,
      originalTransactionId: cardReaderData?.uuid || '',
      taxAmountInCents,
      amountInCents,
      balanceDueInCents,
      isTab: true,
      invoiceNumber: userAttributes?.freedompayInvoiceNumber,
      serviceFee,
      taxRulesBreakdown,
      userAttributes: {
        ...userAttributes,
        isBackBar,
      },
      taxExempt,
    }

    if ((!cardReaderData && !paymentType) || isAddPaymentLater) {
      orderData.payments = []
      delete orderData.invoiceNumber
    }

    const hasOfflineAuth = payment.userAttributes?.isOfflineApproval || false
    dispatch(saveOrder(orderData, true, false, hasOfflineAuth))
  }

  const changeOriginalOfflineTabOrder = (newLineItems, originalOrder) => {
    const { subtotalAmountInCents, taxAmountInCents, amountInCents, balanceDueInCents, serviceFee, taxRulesBreakdown } =
      retotalOfflineOrder(newLineItems)
    // empty line items means a cancelled order, but when offline we still need at least one item
    const emptyItemModels = newLineItems.length === 0 ? [{ ...originalOrder.itemModels?.[0], quantity: 0 }] : null
    const itemModels = emptyItemModels || makeLineItemData(newLineItems)
    const tipAmountInCents = originalOrder?.tipAmountInCents || 0

    let payment
    if (originalOrder?.payments?.length > 0) {
      payment = originalOrder?.payments[0]
      payment.amountInCents = amountInCents + tipAmountInCents
    }

    const updatedOrderData = {
      orderMenuItems: makeCreateOrderLineItems(itemModels),
      itemModels,
      subtotalAmountInCents,
      taxAmountInCents,
      amountInCents,
      balanceDueInCents,
      serviceFee,
      taxRulesBreakdown,
      uuid: originalOrder?.uuid,
      reason: 'Tab Split from POS',
    }

    if (payment) {
      updatedOrderData.payments = [payment]
    }

    if (newLineItems.length === 0) {
      updatedOrderData.state = STADIUM_ORDER_STATES.CANCELLED
    }

    dispatch(saveOrder(updatedOrderData))
    dispatch(setOrderInProgressWithId(originalOrder?.uuid))
  }

  return {
    getSplitOrderTotal,
    splitOrder,
    initiateTab,
  }
}
